import React, {Fragment, useEffect, useState} from "react";
import Breadcrumb from "../../../../layout/breadcrumb";
import {Card, CardBody, Col, Container, FormGroup, Label, Nav, Row} from "reactstrap";
import {Form} from "react-bootstrap";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {toast} from "react-toastify";
import CommonDataTable from "../../../../share-components/table/commonDataTable";
import ModalFormSubmit from "../../../../share-components/form/modalFormSubmit";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
    addImageTagTypeApi,
    deleteImageTagTypeApi,
    getImageTagTypesApi,
    updateImageTagTypeApi
} from "../../../../services/manage-product/setting/imageTagTypeService";

const defaultValues = {
    id: '',
    name: '',
};

const schema = yup.object().shape({
    name: yup.string().trim().required('You must enter a name'),
});

const closedDialog = {type: 'new', props: {isOpen: false}, data: null};

/**
 * Product Settings list for the per-image tags shown on the product edit Images section.
 * Used twice: View Types (slug image-view-type) and Shot Types (slug image-shot-type).
 */
const ImageTagType = ({slug, title, singular}) => {
    const [rows, setRows] = useState([]);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(closedDialog);
    const [buttonDisable, setButtonDisable] = useState(false);

    const dataMenu = [
        {type: 0, text: 'Products', link: '/product'},
        {type: 1, text: 'Product Setting', link: ''},
        {type: 1, text: title, link: ''}
    ];

    const load = () => getImageTagTypesApi(slug, {name}).then(res => {
        setRows(res.response || []);
        setLoading(false);
    }).catch(() => setLoading(false));

    useEffect(() => {
        load();
    }, [slug, name]);

    const {handleSubmit, formState, reset, register, setValue, getValues} = useForm({
        mode: 'onChange',
        defaultValues,
        resolver: yupResolver(schema)
    });
    const {errors} = formState;

    const tableColumns = [
        {
            name: `${singular} Name`,
            selector: row => row.name,
            sortable: true,
            left: true,
        },
    ];

    const onSubmitForm = (dialog, data) => {
        setButtonDisable(true);
        const request = dialog.type === 'new'
            ? addImageTagTypeApi(slug, {name: data.name})
            : updateImageTagTypeApi(slug, {name: data.name}, data.id);
        request.then(() => {
            toast.success(`${singular} ${dialog.type === 'new' ? 'added' : 'updated'} successfully`, {
                position: toast.POSITION.TOP_RIGHT,
            });
            setDialog(closedDialog);
            load();
        }).catch(() => {}).finally(() => setButtonDisable(false));
    };

    const onDelete = (row) => {
        deleteImageTagTypeApi(slug, row.id).then(() => {
            toast.success(`${singular} deleted successfully`, {
                position: toast.POSITION.TOP_RIGHT,
            });
            load();
        }).catch(() => {});
    };

    return (
        <Fragment>
            <Container fluid={true} className="product-wrapper">
                {loading ? <PreLoader></PreLoader> : <>
                    <Breadcrumb data={dataMenu} title={title}/>
                    <div className="product-grid">
                        <div className="feature-products">
                            <Row>
                                <Col sm="12">
                                    <Card>
                                        <Row>
                                            <Col sm="6">
                                                <Nav tabs className="border-tab"></Nav>
                                            </Col>
                                            <Col sm="6">
                                                <div className='float-sm-right p-3'>
                                                    <a className="btn btn-sm btn-primary"
                                                       onClick={() => setDialog({type: 'new', props: {isOpen: true}, data: null})}
                                                    > <i className="fa fa-plus"></i> Add {singular}</a>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                            </Row>
                            <Row>
                                <Col xl="12" sm="12">
                                    <Form onSubmit={(e) => e.preventDefault()}>
                                        <FormGroup>
                                            <input onChange={(e) => setName(e.target.value)} name="name"
                                                   type="text" className="form-control border"
                                                   placeholder={`Search By ${singular} Name`}/>
                                            <i className="fa fa-search"></i>
                                        </FormGroup>
                                    </Form>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="12">
                                    <Card>
                                        <CardBody>
                                            <div className="table-responsive product-table mt-4">
                                                <CommonDataTable
                                                    headerColumns={tableColumns}
                                                    gridData={rows.map(row => ({id: row.id, name: row.name}))}
                                                    pagination
                                                    onEdit={(row) => setDialog({type: 'edit', props: {isOpen: true}, data: row})}
                                                    onDelete={onDelete}
                                                />
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </>}
            </Container>
            <ModalFormSubmit
                defaultValues={defaultValues}
                schema={schema}
                onSubmitForm={onSubmitForm}
                reset={reset}
                formState={formState}
                handleSubmit={handleSubmit}
                title={singular}
                isUseSelector
                dialog={dialog}
                closeDialog={() => setDialog(closedDialog)}
                setValue={setValue}
                getValues={getValues}
                buttonDisable={buttonDisable}
            >
                <FormGroup>
                    <Label className="col-form-label">{singular} Name</Label>
                    <input name="name" type="text" {...register('name')} className={`form-control ${errors.name ? 'is-invalid' : ''}`}/>
                    <div className="invalid-feedback">{errors.name?.message}</div>
                </FormGroup>
            </ModalFormSubmit>
        </Fragment>
    );
};

export const ImageViewTypes = () => <ImageTagType slug="image-view-type" title="View Types" singular="View Type"/>;
export const ImageShotTypes = () => <ImageTagType slug="image-shot-type" title="Shot Types" singular="Shot Type"/>;

export default ImageTagType;
